import React, {
  FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import AgoraRTC, { IAgoraRTCRemoteUser, ILocalAudioTrack, ILocalVideoTrack } from 'agora-rtc-sdk-ng';
import {
  concat, defer, EMPTY, from, interval, Observable, of, timer,
} from 'rxjs';
import { finalize, map, mergeMap } from 'rxjs/operators';
import styled from 'styled-components';
import { CircularProgress, FormControl } from '@material-ui/core';
import { Group } from '@material-ui/icons';
import { useObservable } from 'react-use';
import LiveStreamBoothExhibitorPanelBottomBar
  from 'components/pictureInPicture/LiveStreamBoothExhibitorPanel/LiveStreamBoothExhibitorPanelBottomBar';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { selectXtraEvent } from 'models/cms/events';
import { LIVESTREAM_REALTIMEDB_API_URL, LIVESTREAM_TIMESTAMP_UPDATE_INTERVAL, PROXY_WORKER_URL } from 'appenv';

const StyledPlayerContainer = styled.div`
  width: 100%;
  height: 100%;
  background: gray;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
  flex: 1 1 auto;
`;

const StyledPlayer = styled.div<{ hide?: boolean }>`
  height: 100%;
  width: 100%;
  ${({ hide }) => (hide ? 'display: none;' : '')}
`;

const StyledFormControl = styled(FormControl)`
  width: 200px;
`;

const StyledLabels = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: stretch;
`;

const StyledLabel = styled.div`
  color: white;
  border-radius: 5px;
  padding: 5px 7px;
  display: flex;
  align-items: center;

  &:not(:last-child) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &:not(:first-child) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  & > :not(:last-child) {
    margin-right: 8px;
  }
`;

const StyledAudienceLabel = styled(StyledLabel)<{ isStreaming: boolean }>`
  background: ${({ isStreaming }) => (isStreaming ? '#cf2f2f' : '#55a40d')};
`;

const StyledStatusLabel = styled(StyledLabel)<{ isStreaming: boolean }>`
  background: ${({ isStreaming }) => (isStreaming ? '#ef5050' : '#7acf2f')};
`;

const StyledSpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 100px 0;
  width: 100%;
  height: 100%;
`;

const StyledContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export interface LiveStreamBoothExhibitorPanelContentProps {
  agoraToken: string;
  agoraAccount: string;
  boothId: string;
  realtimeDBToken: string;
}

const EMPTY_PERIODS = [];

const LiveStreamBoothExhibitorPanelContent: FC<LiveStreamBoothExhibitorPanelContentProps> = ({
  agoraToken, agoraAccount, boothId, realtimeDBToken,
}) => {
  const client = useMemo(() => AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' }), []);
  const eventId = useSelector(selectXtraEvent)?.id;

  const playerRef = useRef<HTMLDivElement>();

  const [connected, setConnected] = useState(false);

  const [selectedCameraId, setSelectedCameraId] = useState<string>();
  const [isCameraOn, setIsCameraOn] = useState(true);
  const videoTrack$: Observable<ILocalVideoTrack> = useMemo(() => (!isCameraOn || selectedCameraId == null ? of(null)
    : defer(() => from(AgoraRTC.createCameraVideoTrack({ cameraId: selectedCameraId }))
      .pipe(mergeMap((track) => of(track).pipe(
        mergeMap(() => new Observable<ILocalVideoTrack>((subscriber) => subscriber.next(track))),
        finalize(() => {
          track.close();
          track.stop();
        }),
      ))))), [isCameraOn, selectedCameraId]);
  const videoTrack = useObservable(videoTrack$);

  useEffect(() => {
    videoTrack?.play(playerRef.current, { fit: 'contain' });
    return () => videoTrack?.stop();
  }, [videoTrack]);

  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<string>();
  const [isMicrophoneOn, setIsMicrophoneOn] = useState(true);
  const audioTrack$ = useMemo(() => (!isMicrophoneOn || selectedMicrophoneId == null ? of(null)
    : defer(() => from(AgoraRTC.createMicrophoneAudioTrack({ microphoneId: selectedMicrophoneId }))
      .pipe(mergeMap((track) => of(track).pipe(
        mergeMap(() => new Observable<ILocalAudioTrack>((subscriber) => subscriber.next(track))),
        finalize(() => {
          track.close();
          track.stop();
        }),
      ))))), [isMicrophoneOn, selectedMicrophoneId]);
  const audioTrack = useObservable(audioTrack$);

  const updateLastStreamingTimeStampRef = useRef((isClean) => axios.patch(
    `${PROXY_WORKER_URL}/events/${eventId}/channels/booths/${boothId}.json?eventx_url=${LIVESTREAM_REALTIMEDB_API_URL}&auth=${realtimeDBToken}`, {
      lastStreamingTimeStamp: isClean ? null : { '.sv': 'timestamp' },
    }, {
      headers: {},
    },
  ));

  useEffect(() => {
    updateLastStreamingTimeStampRef.current = (isClean) => axios.patch(
      `${PROXY_WORKER_URL}/events/${eventId}/channels/booths/${boothId}.json?eventx_url=${LIVESTREAM_REALTIMEDB_API_URL}&auth=${realtimeDBToken}`, {
        lastStreamingTimeStamp: isClean ? null : { '.sv': 'timestamp' },
      }, {
        headers: {},
      },
    );
  }, [boothId, eventId, realtimeDBToken]);

  const [isStreaming, setIsStreaming] = useState(false);

  const statusUpdate$ = useMemo(() => (isStreaming ? timer(0, LIVESTREAM_TIMESTAMP_UPDATE_INTERVAL).pipe(
    mergeMap(() => updateLastStreamingTimeStampRef.current(false)),
  ) : EMPTY), [isStreaming]);
  useObservable(statusUpdate$);

  useEffect(() => {
    if (!isStreaming) return undefined;
    const listener = () => {
      navigator.sendBeacon(`${PROXY_WORKER_URL}/events/${eventId}/channels/booths/${boothId}.json`
        + `?eventx_url=${LIVESTREAM_REALTIMEDB_API_URL}`
        + `&auth=${realtimeDBToken}`
        + '&x-http-method-override=PUT', 'null');
    };
    window.addEventListener('beforeunload', listener, false);
    return () => window.removeEventListener('beforeunload', listener);
  }, [boothId, eventId, isStreaming, realtimeDBToken]);

  useEffect(() => {
    if (isStreaming) return () => updateLastStreamingTimeStampRef.current(true);
    return undefined;
  }, [isStreaming]);

  useEffect(() => {
    if (!isStreaming || videoTrack == null) return undefined;
    client.publish(videoTrack);
    return () => client.unpublish(videoTrack);
  }, [client, isStreaming, videoTrack]);

  useEffect(() => {
    if (!isStreaming || audioTrack == null) return undefined;
    client.publish(audioTrack);
    return () => client.unpublish(audioTrack);
  }, [audioTrack, client, isStreaming]);

  const agoraTokenRef = useRef(agoraToken);

  useEffect(() => {
    agoraTokenRef.current = agoraToken;
  }, [agoraToken]);

  useEffect(() => {
    const disposable = concat(
      client.join('ca2da7915d814be6a62b202480315749', `booth-${boothId}`, agoraTokenRef.current, agoraAccount),
    ).subscribe(() => setConnected(true));
    return () => disposable.unsubscribe();
  }, [agoraAccount, boothId, client]);

  const periods = useSelector((state: any) => state.eventConfigs)?.liveStream?.boothIdLiveStreamPeriodsMap?.[boothId] ?? EMPTY_PERIODS;

  const duringPeriods$ = useMemo(() => interval(500).pipe(
    map(() => periods.find(
      ({ startTime, endTime }) => (new Date(startTime) < new Date() && new Date() < new Date(endTime)),
    ) !== undefined),
  ), [periods]);

  const duringPeriods = useObservable(duringPeriods$);
  useEffect(() => {
    if (isStreaming && !duringPeriods) setIsStreaming(false);
  }, [duringPeriods, isStreaming, setIsStreaming]);

  useEffect(() => {
    if (client.channelName != null && client) client.renewToken(agoraToken);
  }, [client, agoraToken]);

  useEffect(() => () => client.leave(), [client]);

  const liveStreamUsers$ = useMemo(() => new Observable<IAgoraRTCRemoteUser[]>((subscriber) => {
    const updateRemoteUsers = () => {
      subscriber.next(client.remoteUsers.filter((it) => !(it.uid as string).startsWith('liveStatusChecker-')));
    };
    client.on('user-joined', () => updateRemoteUsers());
    client.on('user-left', () => updateRemoteUsers());
  }), [client]);

  const liveStreamUsersAmount = useObservable(liveStreamUsers$)?.length ?? 0;

  const haveVideoTrackStreaming$ = useMemo(() => new Observable<boolean>((subscriber) => {
    client.on('user-published', (user, mediaType) => {
      if (user.uid !== client.uid && mediaType === 'video') subscriber.next(true);
    });
    client.on('user-unpublished', (user, mediaType) => {
      if (user.uid !== client.uid && mediaType === 'video') subscriber.next(false);
    });
  }), [client]);

  const haveVideoTrackStreaming = useObservable(haveVideoTrackStreaming$) ?? client.remoteUsers.find((it) => it.hasVideo) != null;

  return !connected ? (
    <StyledSpinnerContainer>
      <CircularProgress />
    </StyledSpinnerContainer>
  ) : (
    <StyledContainer>
      <StyledPlayerContainer>
        <div>
          {haveVideoTrackStreaming && 'Another user is streaming the video track'}
        </div>
        <div>
          {!haveVideoTrackStreaming && (
            videoTrack == null && (
              audioTrack == null
                ? 'Click video or voice button to start live streaming'
                : 'Audio streaming'
            )
          )}
        </div>
        <StyledPlayer ref={playerRef} hide={videoTrack == null} />
        <StyledLabels>
          <StyledStatusLabel isStreaming={isStreaming}>
            {isStreaming ? 'Live' : 'Preview'}
          </StyledStatusLabel>
          {isStreaming && (
            <StyledAudienceLabel isStreaming={isStreaming}>
              <Group />
              <div>
                {liveStreamUsersAmount}
              </div>
            </StyledAudienceLabel>
          )}
        </StyledLabels>
      </StyledPlayerContainer>
      <LiveStreamBoothExhibitorPanelBottomBar
        selectedCameraId={selectedCameraId}
        setSelectedCameraId={setSelectedCameraId}
        isCameraOn={isCameraOn}
        setIsCameraOn={setIsCameraOn}
        selectedMicrophoneId={selectedMicrophoneId}
        setSelectedMicrophoneId={setSelectedMicrophoneId}
        isMicrophoneOn={isMicrophoneOn}
        setIsMicrophoneOn={setIsMicrophoneOn}
        client={client}
        audioTrack={audioTrack}
        isStreaming={isStreaming}
        setIsStreaming={setIsStreaming}
        boothId={boothId}
        duringPeriods={duringPeriods}
      />
    </StyledContainer>
  );
};

export default React.memo(LiveStreamBoothExhibitorPanelContent);
